'use client';

import { useSession, signOut } from 'next-auth/react';
import { useState, useEffect } from 'react';
import { X, LogOut, CreditCard, User, Sparkles, Calendar, ShoppingCart, Zap, AlertTriangle, FileText, Settings, Save, Crown, Heart, Star, Coins } from 'lucide-react';
import Link from 'next/link';

// Latest pricing from CREDIT_PACKS in site-config.ts
const PRICING_PACKAGES = [
  {
    type: 'credits10',
    name: 'Starter',
    credits: 10,
    price: '$15',
    priceValue: 15,
    pricePerBook: '$1.50',
    features: ['10 Credits', 'Coloring & Audio Picture Books', 'No Watermarks', 'High Quality', 'Forever Access'],
  },
  {
    type: 'credits25',
    name: 'Family',
    credits: 25,
    price: '$30',
    priceValue: 30,
    pricePerBook: '$1.20',
    features: ['25 Credits', 'Coloring & Audio Picture Books', 'No Watermarks', 'High Quality', 'Forever Access'],
    featured: true,
  },
  {
    type: 'credits60',
    name: 'Pro',
    credits: 60,
    price: '$60',
    priceValue: 60,
    pricePerBook: '$1.00',
    features: ['60 Credits', 'Coloring & Audio Picture Books', 'No Watermarks', 'High Quality', 'Forever Access'],
  },
];

// Subscription plans from SUBSCRIPTIONS in site-config.ts
const SUBSCRIPTION_PLANS = [
  {
    id: 'amaya_plus',
    name: 'Amaya Plus',
    price: '$19.99',
    priceValue: 19.99,
    monthlyCredits: 30,
    features: ['30 Credits/Month', '100+ Audio Story Gallery', 'New Stories Daily', 'Cancel Anytime'],
  },
  {
    id: 'amaya_pro',
    name: 'Amaya Pro',
    price: '$49.99',
    priceValue: 49.99,
    monthlyCredits: 100,
    features: ['100 Credits/Month', 'Everything in Plus', 'Priority Generation (2× Faster)', 'Change Voices', 'Early Access Models'],
    featured: true,
  },
];

interface Generation {
  id: string;
  prompt: string;
  pages: number;
  status: string;
  watermark: boolean;
  pdfUrl: string | null;
  previewUrls: string | null;
  createdAt: string;
  ageBucket: string | null;
  hashtags: string;
}

interface AudioBook {
  id: string;
  title: string;
  coverImageUrl: string | null;
  pageCount: number;
  totalDuration: number | null;
  createdAt: string;
  watermark: boolean;
}

interface Order {
  id: string;
  userId: string | null;
  email: string;
  stripeId: string;
  kind: string;
  quantity: number;
  amountUsd: number;
  createdAt: string;
}

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AGE_RANGES = [
  { value: 'AGE_0_1', label: '0-1' },
  { value: 'AGE_1_2', label: '1-2' },
  { value: 'AGE_3_4', label: '3-4' },
  { value: 'AGE_5_6', label: '5-6' },
  { value: 'AGE_7_8', label: '7-8' },
  { value: 'AGE_9_10', label: '9-10' },
  { value: null, label: 'None' },
];

// Helper function to format age range for display
function formatAgeRange(ageRange: string | null): string {
  if (!ageRange) return 'Not set';
  const ageMap: Record<string, string> = {
    'AGE_0_1': '0-1 years',
    'AGE_1_2': '1-2 years',
    'AGE_3_4': '3-4 years',
    'AGE_5_6': '5-6 years',
    'AGE_7_8': '7-8 years',
    'AGE_9_10': '9-10 years',
  };
  return ageMap[ageRange] || ageRange;
}

export function AccountSettingsModal({ isOpen, onClose }: AccountSettingsModalProps) {
  const { data: session, update: updateSession } = useSession();
  const [activeTab, setActiveTab] = useState<'account' | 'subscription' | 'purchases' | 'danger'>('account');
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [purchases, setPurchases] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(0);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [plan, setPlan] = useState<'NONE' | 'AMAYA_PLUS' | 'AMAYA_PRO'>('NONE');
  const [nextTopUp, setNextTopUp] = useState<string | null>(null);
  
  // Child profile editing states
  const [kidName, setKidName] = useState('');
  const [kidAgeRange, setKidAgeRange] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Extract user info from session
  const name = session?.user?.name;
  const email = session?.user?.email;

  useEffect(() => {
    if (isOpen && session) {
      fetchCredits();
      setKidName(session.user?.kidName || '');
      setKidAgeRange(session.user?.kidAgeRange || null);
      
      if (activeTab === 'purchases') {
        fetchPurchases();
      }
    }
  }, [isOpen, session, activeTab]);

  async function fetchCredits() {
    try {
      const res = await fetch('/api/account');
      if (res.ok) {
        const data = await res.json();
        setCredits(data.credits || 0);
        if (data.subscriptionPlan) setPlan(data.subscriptionPlan);
        if (data.subscriptionNextTopUp) setNextTopUp(data.subscriptionNextTopUp);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  }

  async function fetchPurchases() {
    try {
      setLoading(true);
      const res = await fetch('/api/account/purchases');
      if (res.ok) {
        const data = await res.json();
        setPurchases(data.purchases || []);
      }
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveProfile() {
    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess(false);

    try {
      const res = await fetch('/api/account', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kidName,
          kidAgeRange,
        }),
      });

      if (res.ok) {
        setProfileSuccess(true);
        await updateSession();
        setTimeout(() => setProfileSuccess(false), 3000);
      } else {
        const data = await res.json();
        setProfileError(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      setProfileError('Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleDeleteAccount() {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const res = await fetch('/api/account', {
        method: 'DELETE',
      });

      if (res.ok) {
        alert('Account deleted successfully');
        await signOut({ redirect: true, callbackUrl: '/' });
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }

  async function handlePurchase(packageType: string) {
    setPurchasing(packageType);
    try {
      const response = await fetch('/api/checkout/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packId: packageType }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to start checkout');
        setPurchasing(null);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
      setPurchasing(null);
    }
  }

  async function handleSubscribe(plan: 'amaya_plus' | 'amaya_pro') {
    setPurchasing(plan);
    try {
      const response = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to start subscription');
        setPurchasing(null);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
      setPurchasing(null);
    }
  }

  if (!isOpen) return null;

  return (
    <>
      {/* 2026 Award-Winning Transparent Overlay */}
      <div className="modal-overlay-2026" onClick={onClose}>
        {/* Subtle Gradient Glow */}
        <div className="ambient-glow"></div>
      </div>

      {/* Compact Award-Winning Modal Container */}
      <div className="modal-container-2026" onClick={(e) => e.stopPropagation()}>
        {/* Elegant Close Button */}
        <button onClick={onClose} className="close-btn-2026" aria-label="Close">
          <X size={18} />
        </button>

        {/* User Avatar & Name Header */}
        <div className="profile-header-2026">
          <div className="avatar-container-2026">
            <div className="avatar-glow"></div>
            <User size={32} />
          </div>
          <h3 className="user-name-2026">{name || email?.split('@')[0] || 'User'}</h3>
          <p className="user-email-2026">{email}</p>
        </div>

        {/* Compact Credits Display */}
        <div className="credits-compact-2026">
          <div className="credits-icon-2026">
            <Coins size={20} />
          </div>
          <div className="credits-text-2026">
            <span className="credits-label-2026">Credits</span>
            <span className="credits-value-2026">{credits}</span>
          </div>
          {plan !== 'NONE' && (
            <div className="plan-badge-2026">
              <Crown size={12} />
              <span>{plan === 'AMAYA_PRO' ? 'Pro' : 'Plus'}</span>
            </div>
          )}
        </div>

        {/* Compact Info Display */}
        <div className="info-section-2026">
          {kidName && (
            <div className="info-row-2026">
              <Heart size={16} className="info-icon-2026" />
              <div className="info-content-2026">
                <span className="info-label-2026">Child's Name</span>
                <span className="info-value-2026">{kidName}</span>
              </div>
            </div>
          )}
          {kidAgeRange && (
            <div className="info-row-2026">
              <Sparkles size={16} className="info-icon-2026" />
              <div className="info-content-2026">
                <span className="info-label-2026">Age Range</span>
                <span className="info-value-2026">{formatAgeRange(kidAgeRange)}</span>
              </div>
            </div>
          )}
          {plan !== 'NONE' && (
            <div className="info-row-2026">
              <Crown size={16} className="info-icon-2026" />
              <div className="info-content-2026">
                <span className="info-label-2026">Subscription</span>
                <span className="info-value-2026">{plan === 'AMAYA_PRO' ? 'Amaya Pro' : 'Amaya Plus'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Award-Winning Action Buttons */}
        <div className="action-buttons-2026">
          <Link href="/account" onClick={onClose}>
            <button className="action-btn-2026 primary">
              <Settings size={18} />
              <span>Full Account Settings</span>
              <div className="btn-shimmer"></div>
            </button>
          </Link>
          
          <button 
            onClick={() => signOut({ callbackUrl: '/' })} 
            className="action-btn-2026 secondary"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>

        {/* Old content removed for compact design */}
        <div style={{ display: 'none' }}>
          {activeTab === 'account' && (
            <div className="tab-content fade-in">
              <div className="section-card-2026">
                <div className="section-header-2026">
                  <div className="section-icon-wrapper">
                    <User size={20} />
                  </div>
                  <div>
                    <h3 className="section-title">Your Profile</h3>
                    <p className="section-subtitle">Update your account information</p>
                  </div>
                </div>

                <div className="form-grid-2026">
                  <div className="form-group-2026">
                    <label className="form-label-2026">
                      <User size={16} />
                      Your Name
                    </label>
                    <div className="input-wrapper-2026">
                      <input
                        type="text"
                        value={session?.user?.name || ''}
                        disabled
                        className="input-2026"
                        placeholder="Your name"
                      />
                      <div className="input-shine"></div>
                    </div>
                    <p className="input-hint">Name cannot be changed</p>
                  </div>

                  <div className="form-group-2026">
                    <label className="form-label-2026">
                      <FileText size={16} />
                      Email Address
                    </label>
                    <div className="input-wrapper-2026">
                      <input
                        type="email"
                        value={session?.user?.email || ''}
                        disabled
                        className="input-2026"
                        placeholder="your@email.com"
                      />
                      <div className="input-shine"></div>
                    </div>
                    <p className="input-hint">Email cannot be changed</p>
                  </div>
                </div>
              </div>

              {/* Child Profile Section */}
              <div className="section-card-2026">
                <div className="section-header-2026">
                  <div className="section-icon-wrapper child">
                    <Heart size={20} />
                  </div>
                  <div>
                    <h3 className="section-title">Child Profile</h3>
                    <p className="section-subtitle">Personalize stories for your child</p>
                  </div>
                </div>

                <div className="form-grid-2026">
                  <div className="form-group-2026">
                    <label className="form-label-2026">
                      <Star size={16} />
                      Child's Name
                    </label>
                    <div className="input-wrapper-2026">
                      <input
                        type="text"
                        value={kidName}
                        onChange={(e) => setKidName(e.target.value)}
                        className="input-2026"
                        placeholder="Enter child's name"
                        maxLength={50}
                      />
                      <div className="input-shine"></div>
                    </div>
                  </div>

                  <div className="form-group-2026">
                    <label className="form-label-2026">
                      <Calendar size={16} />
                      Age Range
                    </label>
                    <div className="select-wrapper-2026">
                      <select
                        value={kidAgeRange || ''}
                        onChange={(e) => setKidAgeRange(e.target.value || null)}
                        className="select-2026"
                      >
                        <option value="">Select age range</option>
                        {AGE_RANGES.map((range) => (
                          <option key={range.label} value={range.value || ''}>
                            {range.label}
                          </option>
                        ))}
                      </select>
                      <div className="select-arrow"></div>
                    </div>
                  </div>
                </div>

                {profileSuccess && (
                  <div className="alert-success">
                    <div className="alert-icon">✓</div>
                    <div className="alert-content">
                      <div className="alert-title">Success!</div>
                      <div className="alert-message">Profile updated successfully</div>
                    </div>
                  </div>
                )}

                {profileError && (
                  <div className="alert-error">
                    <div className="alert-icon">✕</div>
                    <div className="alert-content">
                      <div className="alert-title">Error</div>
                      <div className="alert-message">{profileError}</div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="btn-primary-2026"
                >
                  <div className="btn-shine"></div>
                  <div className="btn-content">
                    {savingProfile ? (
                      <>
                        <div className="spinner"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        <span>Save Changes</span>
                      </>
                    )}
                  </div>
                </button>
              </div>

              {/* Link to Full Account Settings */}
              <div style={{
                marginTop: '32px',
                paddingTop: '24px',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                textAlign: 'center',
              }}>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginBottom: '16px',
                }}>
                  For subscription, purchase history, and more
                </p>
                <Link href="/account" onClick={onClose}>
                  <button
                    style={{
                      padding: '12px 28px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '2px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <Settings size={18} />
                    View Full Account Settings
                  </button>
                </Link>
              </div>
            </div>
          )}

          {/* Subscription Tab (kept for compatibility but hidden) */}
          {activeTab === 'subscription' && (
            <div className="tab-content fade-in">
              <div className="section-card-2026">
                <div className="section-header-2026">
                  <div className="section-icon-wrapper premium">
                    <Crown size={20} />
                  </div>
                  <div>
                    <h3 className="section-title">Your Subscription</h3>
                    <p className="section-subtitle">Current plan and credits</p>
                  </div>
                </div>

                {plan !== 'NONE' ? (
                  <div className="subscription-active-card">
                    <div className="subscription-badge-large">
                      <Crown size={24} />
                      <span>{plan === 'AMAYA_PRO' ? 'Amaya Pro' : 'Amaya Plus'}</span>
                    </div>
                    <div className="subscription-details">
                      <div className="subscription-detail-item">
                        <div className="detail-label">Monthly Credits</div>
                        <div className="detail-value">
                          {plan === 'AMAYA_PRO' ? '100' : '30'} credits
                        </div>
                      </div>
                      {nextTopUp && (
                        <div className="subscription-detail-item">
                          <div className="detail-label">Next Top-Up</div>
                          <div className="detail-value">
                            {new Date(nextTopUp).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="subscription-note">
                      Manage your subscription in your Stripe account
                    </p>
                  </div>
                ) : (
                  <div className="subscription-empty">
                    <div className="empty-icon-large">
                      <Crown size={48} />
                    </div>
                    <h4 className="empty-title">No Active Subscription</h4>
                    <p className="empty-text">
                      Subscribe to get monthly credits and exclusive features
                    </p>
                  </div>
                )}
              </div>

              {/* Subscription Plans */}
              <div className="section-card-2026">
                <div className="section-header-2026">
                  <div className="section-icon-wrapper">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <h3 className="section-title">Available Plans</h3>
                    <p className="section-subtitle">Choose your perfect subscription</p>
                  </div>
                </div>

                <div className="plans-grid-2026">
                  {SUBSCRIPTION_PLANS.map((subPlan) => (
                    <div
                      key={subPlan.id}
                      className={`plan-card-2026 ${subPlan.featured ? 'featured' : ''}`}
                    >
                      {subPlan.featured && (
                        <div className="plan-badge-top">
                          <Star size={12} />
                          <span>Most Popular</span>
                        </div>
                      )}
                      
                      <div className="plan-card-bg"></div>
                      
                      <div className="plan-header-2026">
                        <div className="plan-name">{subPlan.name}</div>
                        <div className="plan-price">
                          <span className="price-currency">$</span>
                          <span className="price-amount">{subPlan.priceValue}</span>
                          <span className="price-period">/mo</span>
                        </div>
                        <div className="plan-credits">{subPlan.monthlyCredits} credits/month</div>
                      </div>

                      <div className="plan-features-2026">
                        {subPlan.features.map((feature, idx) => (
                          <div key={idx} className="plan-feature">
                            <div className="feature-check">✓</div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => handleSubscribe(subPlan.id as 'amaya_plus' | 'amaya_pro')}
                        disabled={purchasing === subPlan.id || plan === subPlan.id.toUpperCase().replace('AMAYA_', 'AMAYA_')}
                        className="btn-plan-2026"
                      >
                        <div className="btn-shine"></div>
                        <span>
                          {plan === subPlan.id.toUpperCase().replace('AMAYA_', 'AMAYA_')
                            ? 'Current Plan'
                            : purchasing === subPlan.id
                            ? 'Loading...'
                            : 'Subscribe Now'}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Credit Packs */}
              <div className="section-card-2026">
                <div className="section-header-2026">
                  <div className="section-icon-wrapper">
                    <CreditCard size={20} />
                  </div>
                  <div>
                    <h3 className="section-title">Credit Packs</h3>
                    <p className="section-subtitle">One-time purchases</p>
                  </div>
                </div>

                <div className="credits-grid-2026">
                  {PRICING_PACKAGES.map((pkg) => (
                    <div
                      key={pkg.type}
                      className={`credit-card-2026 ${pkg.featured ? 'featured' : ''}`}
                    >
                      {pkg.featured && (
                        <div className="credit-badge-top">⭐ Most Popular</div>
                      )}
                      
                      <div className="credit-card-header">
                        <div className="credit-name">{pkg.name}</div>
                        <div className="credit-price">{pkg.price}</div>
                        <div className="credit-count">{pkg.credits} credits</div>
                        <div className="credit-rate">{pkg.pricePerBook}/credit</div>
                      </div>

                      <div className="credit-features">
                        {pkg.features.map((feature, idx) => (
                          <div key={idx} className="credit-feature">
                            <div className="feature-dot"></div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => handlePurchase(pkg.type)}
                        disabled={purchasing === pkg.type}
                        className="btn-credit-2026"
                      >
                        <div className="btn-shine"></div>
                        <span>
                          {purchasing === pkg.type ? 'Loading...' : 'Buy Now'}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Purchases Tab */}
          {activeTab === 'purchases' && (
            <div className="tab-content fade-in">
              <div className="section-card-2026">
                <div className="section-header-2026">
                  <div className="section-icon-wrapper">
                    <ShoppingCart size={20} />
                  </div>
                  <div>
                    <h3 className="section-title">Purchase History</h3>
                    <p className="section-subtitle">Your transaction records</p>
                  </div>
                </div>

                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading purchases...</p>
                  </div>
                ) : purchases.length === 0 ? (
                  <div className="empty-state-2026">
                    <div className="empty-icon-large">
                      <ShoppingCart size={48} />
                    </div>
                    <h4 className="empty-title">No Purchases Yet</h4>
                    <p className="empty-text">
                      Your purchase history will appear here
                    </p>
                  </div>
                ) : (
                  <div className="purchases-list-2026">
                    {purchases.map((purchase) => (
                      <div key={purchase.id} className="purchase-item-2026">
                        <div className="purchase-icon">
                          <CreditCard size={20} />
                        </div>
                        <div className="purchase-details">
                          <div className="purchase-title">
                            {purchase.kind === 'subscription' ? '🌟 Subscription' : '💳 Credit Pack'}
                          </div>
                          <div className="purchase-meta">
                            {new Date(purchase.createdAt).toLocaleDateString()} • {purchase.quantity} credits
                          </div>
                        </div>
                        <div className="purchase-amount">
                          ${(purchase.amountUsd / 100).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && (
            <div className="tab-content fade-in">
              <div className="section-card-2026 danger-zone">
                <div className="section-header-2026">
                  <div className="section-icon-wrapper danger">
                    <AlertTriangle size={20} />
                  </div>
                  <div>
                    <h3 className="section-title">Danger Zone</h3>
                    <p className="section-subtitle">Irreversible actions</p>
                  </div>
                </div>

                {/* Sign Out */}
                <div className="danger-card">
                  <div className="danger-card-content">
                    <LogOut size={24} className="danger-icon" />
                    <div className="danger-info">
                      <h4 className="danger-title">Sign Out</h4>
                      <p className="danger-description">
                        Sign out of your account on this device
                      </p>
                    </div>
                  </div>
                  
                  {!showSignOutConfirm ? (
                    <button
                      onClick={() => setShowSignOutConfirm(true)}
                      className="btn-danger-secondary"
                    >
                      Sign Out
                    </button>
                  ) : (
                    <div className="danger-confirm-group">
                      <button
                        onClick={() => signOut({ redirect: true, callbackUrl: '/' })}
                        className="btn-danger-confirm"
                      >
                        Confirm Sign Out
                      </button>
                      <button
                        onClick={() => setShowSignOutConfirm(false)}
                        className="btn-danger-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {/* Delete Account */}
                <div className="danger-card critical">
                  <div className="danger-card-content">
                    <X size={24} className="danger-icon" />
                    <div className="danger-info">
                      <h4 className="danger-title">Delete Account</h4>
                      <p className="danger-description">
                        Permanently delete your account and all data. This cannot be undone.
                      </p>
                    </div>
                  </div>

                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="btn-danger-critical"
                    >
                      Delete Account
                    </button>
                  ) : (
                    <div className="delete-confirm-section">
                      <div className="delete-warning">
                        <AlertTriangle size={20} />
                        <p>
                          This action is <strong>permanent</strong>. All your data, books, and credits will be lost.
                        </p>
                      </div>
                      
                      <div className="form-group-2026">
                        <label className="form-label-2026">
                          Type <strong>DELETE</strong> to confirm
                        </label>
                        <div className="input-wrapper-2026">
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            className="input-2026"
                            placeholder="DELETE"
                          />
                        </div>
                      </div>

                      <div className="danger-confirm-group">
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                          className="btn-danger-confirm"
                        >
                          {isDeleting ? 'Deleting...' : 'Permanently Delete'}
                        </button>
                        <button
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText('');
                          }}
                          className="btn-danger-cancel"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx global>{`
        /* ========================================
           2026 AWARD-WINNING COMPACT MODAL
           Ultra-Transparent, Ultra-Modern
           ======================================== */

        /* Transparent Overlay with Blur */
        .modal-overlay-2026 {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          z-index: 999998;
          display: flex;
          align-items: center;
          justifycontent: center;
          animation: fadeIn 0.2s ease;
        }

        .ambient-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.1), transparent 70%);
          pointer-events: none;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Compact Modal Container */
        .modal-container-2026 {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 90%;
          max-width: 420px;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(40px);
          -webkit-backdrop-filter: blur(40px);
          border-radius: 28px;
          border: 2px solid rgba(180, 160, 229, 0.3);
          box-shadow: 
            0 20px 60px rgba(0, 0, 0, 0.15),
            0 0 0 1px rgba(255, 255, 255, 0.8) inset,
            0 8px 32px rgba(180, 160, 229, 0.2);
          z-index: 999999;
          padding: 32px;
          animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -45%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        /* Close Button */
        .close-btn-2026 {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(100, 116, 139, 0.08);
          border: 1px solid rgba(100, 116, 139, 0.15);
          border-radius: 10px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748B;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 10;
        }

        .close-btn-2026:hover {
          background: rgba(180, 160, 229, 0.15);
          border-color: rgba(180, 160, 229, 0.3);
          color: #B4A0E5;
          transform: rotate(90deg) scale(1.05);
        }

        /* Profile Header */
        .profile-header-2026 {
          text-align: center;
          margin-bottom: 24px;
        }

        .avatar-container-2026 {
          width: 80px;
          height: 80px;
          margin: 0 auto 16px;
          background: linear-gradient(135deg, #B4A0E5, #A8D8E6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          position: relative;
          border: 3px solid rgba(180, 160, 229, 0.3);
          box-shadow: 0 12px 32px rgba(180, 160, 229, 0.35);
        }

        .avatar-glow {
          position: absolute;
          inset: -6px;
          background: linear-gradient(135deg, #B4A0E5, #A8D8E6);
          border-radius: 50%;
          opacity: 0.4;
          filter: blur(16px);
          z-index: -1;
          animation: pulse 3s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.6; }
        }

        .user-name-2026 {
          font-size: 1.5rem;
          font-weight: 900;
          background: linear-gradient(135deg, #B4A0E5, #7B68EE, #A8D8E6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 4px 0;
          letter-spacing: -0.02em;
        }

        .user-email-2026 {
          font-size: 0.875rem;
          color: #64748B;
          margin: 0;
        }

        /* Compact Credits Display */
        .credits-compact-2026 {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 165, 0, 0.15));
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: 16px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          position: relative;
          overflow: hidden;
        }

        .credits-compact-2026::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          to { left: 100%; }
        }

        .credits-icon-2026 {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #FFD700, #FFA500);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
          box-shadow: 0 4px 16px rgba(255, 215, 0, 0.4);
        }

        .credits-text-2026 {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .credits-label-2026 {
          font-size: 0.75rem;
          font-weight: 700;
          color: #92400E;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .credits-value-2026 {
          font-size: 1.75rem;
          font-weight: 900;
          background: linear-gradient(135deg, #FFD700, #FFA500);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
        }

        .plan-badge-2026 {
          background: linear-gradient(135deg, #7C3AED, #A855F7);
          padding: 6px 12px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.75rem;
          font-weight: 700;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        /* Info Section */
        .info-section-2026 {
          background: rgba(201, 182, 228, 0.08);
          border: 2px solid rgba(180, 160, 229, 0.15);
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .info-row-2026 {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .info-icon-2026 {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #B4A0E5, #A8D8E6);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(180, 160, 229, 0.25);
        }

        .info-content-2026 {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .info-label-2026 {
          font-size: 0.875rem;
          color: #64748B;
          font-weight: 600;
        }

        .info-value-2026 {
          font-size: 0.9375rem;
          font-weight: 700;
          background: linear-gradient(135deg, #B4A0E5, #7B68EE);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Action Buttons */
        .action-buttons-2026 {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .action-btn-2026 {
          width: 100%;
          padding: 16px 24px;
          border-radius: 14px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          position: relative;
          overflow: hidden;
        }

        .action-btn-2026.primary {
          background: linear-gradient(135deg, #B4A0E5, #A8D8E6);
          color: white;
          box-shadow: 0 8px 24px rgba(180, 160, 229, 0.35);
          border: 2px solid rgba(255, 255, 255, 0.5);
        }

        .action-btn-2026.primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(180, 160, 229, 0.45);
        }

        .action-btn-2026.primary:active {
          transform: translateY(-1px);
        }

        .action-btn-2026.secondary {
          background: rgba(100, 116, 139, 0.08);
          border: 2px solid rgba(100, 116, 139, 0.15);
          color: #64748B;
        }

        .action-btn-2026.secondary:hover {
          background: rgba(239, 68, 68, 0.1);
          border-color: rgba(239, 68, 68, 0.3);
          color: #EF4444;
          transform: translateY(-2px);
        }

        .btn-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          animation: shimmer 2s infinite;
        }

        /* Responsive */
        @media (max-width: 480px) {
          .modal-container-2026 {
            width: 95%;
            padding: 24px;
          }

          .user-name-2026 {
            font-size: 1.25rem;
          }

          .credits-value-2026 {
            font-size: 1.5rem;
          }
        }

        /* OLD STYLES KEPT FOR COMPATIBILITY */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: linear-gradient(135deg, 
            rgba(102, 126, 234, 0.97) 0%,
            rgba(118, 75, 162, 0.97) 50%,
            rgba(168, 85, 247, 0.97) 100%
          );
          backdrop-filter: blur(30px) saturate(180%);
          -webkit-backdrop-filter: blur(30px) saturate(180%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1.5rem;
          animation: overlayFadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        @keyframes overlayFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Floating Particles */
        .floating-particles {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          width: 6px;
          height: 6px;
          background: radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.4) 100%);
          border-radius: 50%;
          box-shadow: 
            0 0 30px rgba(255, 255, 255, 0.8),
            0 0 60px rgba(168, 216, 230, 0.6);
        }

        .particle-1 {
          top: 20%;
          left: 10%;
          animation: float1 8s ease-in-out infinite;
        }

        .particle-2 {
          top: 60%;
          left: 20%;
          animation: float2 10s ease-in-out infinite;
        }

        .particle-3 {
          top: 40%;
          right: 15%;
          animation: float3 12s ease-in-out infinite;
        }

        .particle-4 {
          bottom: 30%;
          right: 25%;
          animation: float1 9s ease-in-out infinite;
        }

        .particle-5 {
          bottom: 20%;
          left: 30%;
          animation: float2 11s ease-in-out infinite;
        }

        .particle-6 {
          top: 80%;
          right: 40%;
          animation: float3 7s ease-in-out infinite;
        }

        @keyframes float1 {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(30px, -50px); }
          50% { transform: translate(-20px, -100px); }
          75% { transform: translate(40px, -50px); }
        }

        @keyframes float2 {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-40px, 60px); }
          50% { transform: translate(30px, 120px); }
          75% { transform: translate(-30px, 60px); }
        }

        @keyframes float3 {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(50px, 40px); }
          66% { transform: translate(-50px, 80px); }
        }

        /* Animated Gradient Mesh */
        .gradient-mesh {
          position: absolute;
          inset: -50%;
          background: radial-gradient(circle at 20% 30%, rgba(168, 216, 230, 0.3) 0%, transparent 50%),
                      radial-gradient(circle at 80% 70%, rgba(201, 182, 228, 0.3) 0%, transparent 50%),
                      radial-gradient(circle at 50% 50%, rgba(248, 200, 160, 0.2) 0%, transparent 50%);
          animation: meshRotate 20s linear infinite;
          filter: blur(40px);
        }

        @keyframes meshRotate {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.1); }
          100% { transform: rotate(360deg) scale(1); }
        }

        /* Modal Container */
        .modal-container {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 90%;
          max-width: 720px;
          max-height: 85vh;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(60px) saturate(200%);
          -webkit-backdrop-filter: blur(60px) saturate(200%);
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.8);
          box-shadow: 
            0 0 0 1px rgba(255, 255, 255, 0.6) inset,
            0 50px 120px rgba(102, 126, 234, 0.4),
            0 30px 80px rgba(118, 75, 162, 0.3),
            0 0 120px rgba(168, 216, 230, 0.2);
          z-index: 10000;
          display: flex;
          flex-direction: column;
          animation: modalSlideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          overflow: hidden;
        }

        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -45%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        /* Glassmorphic Header */
        .modal-header {
          position: relative;
          padding: 1.25rem 1.5rem;
          background: linear-gradient(135deg, 
            rgba(168, 216, 230, 0.15) 0%,
            rgba(201, 182, 228, 0.15) 100%
          );
          border-bottom: 1px solid rgba(168, 216, 230, 0.3);
          display: flex;
          align-items: center;
          gap: 1rem;
          overflow: hidden;
        }

        .header-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at top left, rgba(168, 216, 230, 0.4) 0%, transparent 70%);
          pointer-events: none;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex: 1;
          position: relative;
          z-index: 1;
        }

        .header-icon-container {
          position: relative;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .header-icon-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 14px;
          animation: iconPulse 3s ease-in-out infinite;
        }

        @keyframes iconPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }

        .header-icon {
          position: relative;
          z-index: 1;
          color: white;
          filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
        }

        .header-text {
          flex: 1;
        }

        .header-title {
          font-size: 1.375rem;
          font-weight: 800;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 0.125rem 0;
          letter-spacing: -0.02em;
        }

        .header-subtitle {
          font-size: 0.8125rem;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        .close-btn {
          position: relative;
          z-index: 1;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          border: 2px solid rgba(168, 216, 230, 0.3);
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .close-btn:hover {
          background: white;
          border-color: #667eea;
          color: #667eea;
          transform: rotate(90deg) scale(1.1);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        /* Floating Credits Card */
        .credits-float-container {
          padding: 0 1.5rem;
          margin-top: -1rem;
          position: relative;
          z-index: 10;
        }

        .credits-card-premium {
          position: relative;
          background: linear-gradient(135deg, 
            #fbbf24 0%,
            #f59e0b 50%,
            #d97706 100%
          );
          border-radius: 18px;
          padding: 1.25rem 1.5rem;
          box-shadow: 
            0 0 0 1px rgba(255, 255, 255, 0.6),
            0 8px 32px rgba(251, 191, 36, 0.5),
            0 20px 60px rgba(217, 119, 6, 0.4),
            inset 0 2px 0 rgba(255, 255, 255, 0.6),
            inset 0 -2px 8px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
          overflow: hidden;
          animation: cardFloat 3s ease-in-out infinite;
          border: 2px solid rgba(255, 255, 255, 0.4);
        }

        @keyframes cardFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-4px) scale(1.01); }
        }

        .credits-card-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
          pointer-events: none;
        }

        .credits-card-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 30%, rgba(255, 255, 255, 0.5) 50%, transparent 70%);
          animation: shine 3s infinite;
          pointer-events: none;
        }

        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .credits-display {
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
          z-index: 1;
        }

        .credits-icon-wrapper {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: iconBounce 2s ease-in-out infinite;
        }

        @keyframes iconBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }

        .credits-icon {
          color: #92400e;
        }

        .credits-info {
          display: flex;
          flex-direction: column;
        }

        .credits-label {
          font-size: 0.6875rem;
          font-weight: 700;
          color: rgba(146, 64, 14, 0.9);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 0.125rem;
          background: none;
          text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
        }

        .credits-value {
          font-size: 1.75rem;
          font-weight: 900;
          color: #78350f;
          line-height: 1;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .plan-badge-premium {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 100px;
          font-size: 0.875rem;
          font-weight: 700;
          color: #667eea;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* Award-Winning Tabs */
        .tabs-container-2026 {
          position: relative;
          display: flex;
          gap: 0.5rem;
          padding: 1rem 1.5rem;
          background: rgba(248, 250, 252, 0.5);
          border-bottom: 1px solid rgba(168, 216, 230, 0.2);
          overflow-x: auto;
          scrollbar-width: none;
        }

        .tabs-container-2026::-webkit-scrollbar {
          display: none;
        }

        .tabs-track {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: rgba(168, 216, 230, 0.2);
        }

        .tab-2026 {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: transparent;
          border: none;
          border-radius: 12px;
          font-size: 0.9375rem;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
        }

        .tab-2026:hover {
          background: rgba(168, 216, 230, 0.1);
          color: #667eea;
          transform: translateY(-2px);
        }

        .tab-2026.active {
          background: linear-gradient(135deg, 
            rgba(168, 216, 230, 0.25) 0%,
            rgba(201, 182, 228, 0.25) 100%
          );
          color: #667eea;
          box-shadow: 
            0 0 0 2px rgba(102, 126, 234, 0.4),
            0 6px 20px rgba(102, 126, 234, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        .tab-indicator {
          position: absolute;
          bottom: -1.5rem;
          left: 50%;
          transform: translateX(-50%);
          width: 40%;
          height: 3px;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
          border-radius: 10px;
          animation: indicatorSlide 0.3s ease;
        }

        @keyframes indicatorSlide {
          from { width: 0%; opacity: 0; }
          to { width: 40%; opacity: 1; }
        }

        /* Content Area */
        .modal-content-2026 {
          flex: 1;
          overflow-y: auto;
          padding: 1.5rem;
          min-height: 0;
        }

        .modal-content-2026::-webkit-scrollbar {
          width: 8px;
        }

        .modal-content-2026::-webkit-scrollbar-track {
          background: rgba(168, 216, 230, 0.1);
          border-radius: 10px;
        }

        .modal-content-2026::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #A8D8E6 0%, #C9B6E4 100%);
          border-radius: 10px;
        }

        .modal-content-2026::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
        }

        .tab-content {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .fade-in {
          animation: fadeIn 0.4s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Section Cards */
        .section-card-2026 {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(30px) saturate(150%);
          -webkit-backdrop-filter: blur(30px) saturate(150%);
          border-radius: 18px;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 0 0 1px rgba(168, 216, 230, 0.2),
            0 10px 40px rgba(102, 126, 234, 0.12),
            0 4px 16px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 1);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .section-card-2026:hover {
          transform: translateY(-6px) scale(1.01);
          box-shadow: 
            0 0 0 1px rgba(168, 216, 230, 0.3),
            0 24px 80px rgba(102, 126, 234, 0.18),
            0 12px 48px rgba(118, 75, 162, 0.15),
            0 0 80px rgba(168, 216, 230, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 1);
          border-color: rgba(102, 126, 234, 0.3);
        }

        .section-header-2026 {
          display: flex;
          align-items: center;
          gap: 0.875rem;
          margin-bottom: 1.25rem;
        }

        .section-icon-wrapper {
          width: 42px;
          height: 42px;
          background: linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 
            0 10px 30px rgba(168, 216, 230, 0.4),
            0 0 40px rgba(201, 182, 228, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.4);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .section-icon-wrapper.child {
          background: linear-gradient(135deg, #F8C8A0 0%, #F8A0C8 100%);
          box-shadow: 
            0 10px 30px rgba(248, 200, 160, 0.5),
            0 0 40px rgba(248, 160, 200, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.4);
        }

        .section-icon-wrapper.premium {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          box-shadow: 
            0 10px 30px rgba(102, 126, 234, 0.5),
            0 0 40px rgba(118, 75, 162, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.4);
        }

        .section-icon-wrapper.danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
        }

        .section-title {
          font-size: 1.25rem;
          font-weight: 800;
          color: #1e293b;
          margin: 0 0 0.125rem 0;
          letter-spacing: -0.01em;
        }

        .section-subtitle {
          font-size: 0.8125rem;
          color: #64748b;
          margin: 0;
        }

        /* Form Elements */
        .form-grid-2026 {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .form-group-2026 {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .form-label-2026 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9375rem;
          font-weight: 600;
          color: #1e293b;
        }

        .input-wrapper-2026 {
          position: relative;
        }

        .input-2026 {
          width: 100%;
          padding: 1rem 1.25rem;
          border: 2px solid rgba(168, 216, 230, 0.3);
          border-radius: 12px;
          font-size: 1rem;
          color: #1e293b;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .input-2026:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1),
                      0 4px 12px rgba(102, 126, 234, 0.15);
          transform: translateY(-2px);
        }

        .input-2026:disabled {
          background: rgba(248, 250, 252, 0.5);
          color: #94a3b8;
          cursor: not-allowed;
        }

        .input-shine {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent 0%,
            rgba(255, 255, 255, 0.8) 50%,
            transparent 100%
          );
          border-radius: 12px 12px 0 0;
        }

        .input-hint {
          font-size: 0.8125rem;
          color: #94a3b8;
          margin: -0.25rem 0 0 0;
        }

        .select-wrapper-2026 {
          position: relative;
        }

        .select-2026 {
          width: 100%;
          padding: 1rem 2.5rem 1rem 1.25rem;
          border: 2px solid rgba(168, 216, 230, 0.3);
          border-radius: 12px;
          font-size: 1rem;
          color: #1e293b;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          cursor: pointer;
          appearance: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .select-2026:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1),
                      0 4px 12px rgba(102, 126, 234, 0.15);
        }

        .select-arrow {
          position: absolute;
          right: 1.25rem;
          top: 50%;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #64748b;
          pointer-events: none;
        }

        /* Alerts */
        .alert-success,
        .alert-error {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          animation: alertSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .alert-success {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%);
          border: 2px solid rgba(34, 197, 94, 0.3);
        }

        .alert-error {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
          border: 2px solid rgba(239, 68, 68, 0.3);
        }

        @keyframes alertSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .alert-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .alert-success .alert-icon {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
        }

        .alert-error .alert-icon {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .alert-content {
          flex: 1;
        }

        .alert-title {
          font-size: 0.9375rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }

        .alert-success .alert-title {
          color: #166534;
        }

        .alert-error .alert-title {
          color: #991b1b;
        }

        .alert-message {
          font-size: 0.875rem;
          color: #64748b;
        }

        /* Buttons */
        .btn-primary-2026 {
          position: relative;
          width: 100%;
          padding: 1.25rem 2rem;
          border: none;
          border-radius: 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 1.0625rem;
          font-weight: 700;
          cursor: pointer;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 
            0 12px 28px rgba(102, 126, 234, 0.4),
            0 4px 16px rgba(118, 75, 162, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-primary-2026:hover:not(:disabled) {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 
            0 16px 48px rgba(102, 126, 234, 0.5),
            0 8px 28px rgba(118, 75, 162, 0.4),
            0 0 60px rgba(168, 216, 230, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.4);
        }

        .btn-primary-2026:active:not(:disabled) {
          transform: translateY(0) scale(0.98);
        }

        .btn-primary-2026:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, 
            transparent 30%,
            rgba(255, 255, 255, 0.3) 50%,
            transparent 70%
          );
          animation: btnShine 3s infinite;
        }

        @keyframes btnShine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .btn-content {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Subscription Active Card */
        .subscription-active-card {
          background: linear-gradient(135deg, 
            rgba(102, 126, 234, 0.1) 0%,
            rgba(118, 75, 162, 0.1) 100%
          );
          border: 2px solid rgba(102, 126, 234, 0.3);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
        }

        .subscription-badge-large {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 100px;
          color: white;
          font-size: 1.25rem;
          font-weight: 800;
          margin-bottom: 2rem;
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        .subscription-details {
          display: flex;
          justify-content: center;
          gap: 3rem;
          margin-bottom: 1.5rem;
        }

        .subscription-detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #667eea;
        }

        .subscription-note {
          font-size: 0.875rem;
          color: #94a3b8;
          margin: 0;
        }

        /* Empty States */
        .empty-state-2026,
        .subscription-empty {
          text-align: center;
          padding: 3rem 2rem;
        }

        .empty-icon-large {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(168, 216, 230, 0.2) 0%, rgba(201, 182, 228, 0.2) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: #667eea;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.75rem 0;
        }

        .empty-text {
          font-size: 1rem;
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        /* Plans Grid */
        .plans-grid-2026 {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .plan-card-2026 {
          position: relative;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(20px);
          border: 2px solid rgba(168, 216, 230, 0.3);
          border-radius: 20px;
          padding: 2rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .plan-card-2026.featured {
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1),
                      0 20px 40px rgba(102, 126, 234, 0.2);
        }

        .plan-card-2026:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .plan-badge-top {
          position: absolute;
          top: 1rem;
          right: 1rem;
          display: flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.75rem;
          background: linear-gradient(135deg, #F8C8A0 0%, #F8A0C8 100%);
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 700;
          color: white;
          box-shadow: 0 4px 12px rgba(248, 200, 160, 0.3);
        }

        .plan-card-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at top right, rgba(168, 216, 230, 0.1) 0%, transparent 60%);
          pointer-events: none;
        }

        .plan-header-2026 {
          position: relative;
          z-index: 1;
          text-align: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(168, 216, 230, 0.2);
        }

        .plan-name {
          font-size: 1.25rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 1rem;
        }

        .plan-price {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 0.25rem;
          margin-bottom: 0.5rem;
        }

        .price-currency {
          font-size: 1.5rem;
          font-weight: 700;
          color: #667eea;
        }

        .price-amount {
          font-size: 3rem;
          font-weight: 900;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
        }

        .price-period {
          font-size: 1.125rem;
          font-weight: 600;
          color: #64748b;
        }

        .plan-credits {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #64748b;
        }

        .plan-features-2026 {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .plan-feature {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.9375rem;
          color: #475569;
        }

        .feature-check {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          flex-shrink: 0;
        }

        .btn-plan-2026 {
          position: relative;
          width: 100%;
          padding: 1rem 2rem;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        .btn-plan-2026:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
        }

        .btn-plan-2026:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Credits Grid */
        .credits-grid-2026 {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
        }

        .credit-card-2026 {
          position: relative;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(20px);
          border: 2px solid rgba(168, 216, 230, 0.3);
          border-radius: 18px;
          padding: 1.5rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          text-align: center;
        }

        .credit-card-2026.featured {
          border-color: #F8C8A0;
          box-shadow: 0 0 0 4px rgba(248, 200, 160, 0.1);
        }

        .credit-card-2026:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
        }

        .credit-badge-top {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          padding: 0.375rem 1rem;
          background: linear-gradient(135deg, #F8C8A0 0%, #F8A0C8 100%);
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 700;
          color: white;
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(248, 200, 160, 0.3);
        }

        .credit-card-header {
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(168, 216, 230, 0.2);
        }

        .credit-name {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.75rem;
        }

        .credit-price {
          font-size: 2.25rem;
          font-weight: 900;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 0.5rem;
        }

        .credit-count {
          font-size: 1rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.25rem;
        }

        .credit-rate {
          font-size: 0.875rem;
          color: #64748b;
        }

        .credit-features {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .credit-feature {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #475569;
        }

        .feature-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #667eea;
          flex-shrink: 0;
        }

        .btn-credit-2026 {
          position: relative;
          width: 100%;
          padding: 0.875rem 1.5rem;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 0.9375rem;
          font-weight: 700;
          cursor: pointer;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3);
        }

        .btn-credit-2026:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(102, 126, 234, 0.4);
        }

        .btn-credit-2026:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Purchases List */
        .purchases-list-2026 {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .purchase-item-2026 {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          background: rgba(255, 255, 255, 0.6);
          border: 1px solid rgba(168, 216, 230, 0.2);
          border-radius: 14px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .purchase-item-2026:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .purchase-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .purchase-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .purchase-title {
          font-size: 1rem;
          font-weight: 700;
          color: #1e293b;
        }

        .purchase-meta {
          font-size: 0.875rem;
          color: #64748b;
        }

        .purchase-amount {
          font-size: 1.5rem;
          font-weight: 800;
          color: #667eea;
          flex-shrink: 0;
        }

        .loading-state {
          text-align: center;
          padding: 3rem 2rem;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(102, 126, 234, 0.2);
          border-top-color: #667eea;
          border-radius: 50%;
          margin: 0 auto 1rem;
          animation: spin 0.8s linear infinite;
        }

        /* Danger Zone */
        .danger-zone {
          border-color: rgba(239, 68, 68, 0.3);
        }

        .danger-card {
          background: rgba(255, 255, 255, 0.6);
          border: 2px solid rgba(239, 68, 68, 0.2);
          border-radius: 14px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .danger-card.critical {
          background: rgba(239, 68, 68, 0.05);
          border-color: rgba(239, 68, 68, 0.3);
        }

        .danger-card-content {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .danger-icon {
          width: 48px;
          height: 48px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ef4444;
          flex-shrink: 0;
        }

        .danger-info {
          flex: 1;
        }

        .danger-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.5rem 0;
        }

        .danger-description {
          font-size: 0.9375rem;
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        .btn-danger-secondary,
        .btn-danger-critical,
        .btn-danger-confirm,
        .btn-danger-cancel {
          padding: 0.875rem 1.5rem;
          border: none;
          border-radius: 10px;
          font-size: 0.9375rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .btn-danger-secondary {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: 2px solid rgba(239, 68, 68, 0.3);
        }

        .btn-danger-secondary:hover {
          background: rgba(239, 68, 68, 0.15);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
        }

        .btn-danger-critical {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }

        .btn-danger-critical:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
        }

        .danger-confirm-group {
          display: flex;
          gap: 0.75rem;
        }

        .btn-danger-confirm {
          flex: 1;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }

        .btn-danger-confirm:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
        }

        .btn-danger-confirm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-danger-cancel {
          flex: 1;
          background: rgba(148, 163, 184, 0.1);
          color: #64748b;
          border: 2px solid rgba(148, 163, 184, 0.3);
        }

        .btn-danger-cancel:hover {
          background: rgba(148, 163, 184, 0.15);
        }

        .delete-confirm-section {
          margin-top: 1.5rem;
        }

        .delete-warning {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.25rem;
          background: rgba(239, 68, 68, 0.1);
          border: 2px solid rgba(239, 68, 68, 0.3);
          border-radius: 10px;
          margin-bottom: 1.5rem;
          color: #991b1b;
        }

        .delete-warning p {
          margin: 0;
          font-size: 0.9375rem;
          line-height: 1.6;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .modal-container {
            width: 95%;
            max-height: 95vh;
            border-radius: 24px;
          }

          .modal-header {
            padding: 1.5rem;
          }

          .header-icon-container {
            width: 52px;
            height: 52px;
          }

          .header-title {
            font-size: 1.375rem;
          }

          .header-subtitle {
            font-size: 0.875rem;
          }

          .credits-float-container {
            padding: 0 1.5rem;
          }

          .credits-card-premium {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }

          .tabs-container-2026 {
            padding: 1rem 1.5rem;
          }

          .tab-2026 span {
            display: none;
          }

          .modal-content-2026 {
            padding: 1.5rem;
          }

          .section-card-2026 {
            padding: 1.5rem;
          }

          .form-grid-2026 {
            grid-template-columns: 1fr;
          }

          .plans-grid-2026,
          .credits-grid-2026 {
            grid-template-columns: 1fr;
          }

          .subscription-details {
            flex-direction: column;
            gap: 1.5rem;
          }

          .danger-confirm-group {
            flex-direction: column;
          }
        }

        @media (max-width: 480px) {
          .modal-container {
            border-radius: 20px;
          }

          .header-title {
            font-size: 1.25rem;
          }

          .credits-value {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.25rem;
          }

          .plan-price .price-amount {
            font-size: 2.5rem;
          }
        }
      `}</style>
    </>
  );
}
